import { useEffect } from "react"
import { useTheme } from "next-themes"
import { Toaster as Sonner } from "sonner"

type ToasterProps = React.ComponentProps<typeof Sonner>

/**
 * Sonner pauses every toast's timer while its stack is "expanded", and it
 * expands on `mouseenter`. A phone fires an emulated mouseover after a tap, and
 * no mouseleave until the next tap lands somewhere else, so one tap on a toast
 * froze the whole stack: every toast after it stayed until swiped away one by
 * one (a teacher saving several session notes in a row, 2026-10-12; still so in
 * sonner 2.0.8). Hover means nothing on touch, so the emulated mouse events
 * that follow a touch inside the toaster are stopped before React sees them.
 * Swipe-to-dismiss uses pointer events and is unaffected.
 */
function useNoHoverPauseOnTouch() {
  useEffect(() => {
    let lastTouch = 0
    const onPointerDown = (e: PointerEvent) => {
      if (e.pointerType === "touch") lastTouch = Date.now()
    }
    const onMouse = (e: MouseEvent) => {
      if (Date.now() - lastTouch > 1000) return
      if (e.target instanceof Element && e.target.closest("[data-sonner-toaster]")) e.stopPropagation()
    }
    window.addEventListener("pointerdown", onPointerDown, true)
    window.addEventListener("mouseover", onMouse, true)
    window.addEventListener("mousemove", onMouse, true)
    return () => {
      window.removeEventListener("pointerdown", onPointerDown, true)
      window.removeEventListener("mouseover", onMouse, true)
      window.removeEventListener("mousemove", onMouse, true)
    }
  }, [])
}

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme()
  useNoHoverPauseOnTouch()

  return (
    <Sonner
      theme={theme as ToasterProps["theme"]}
      className="toaster group"
      toastOptions={{
        classNames: {
          toast:
            "group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg",
          description: "group-[.toast]:text-muted-foreground",
          actionButton:
            "group-[.toast]:bg-primary group-[.toast]:text-primary-foreground",
          cancelButton:
            "group-[.toast]:bg-muted group-[.toast]:text-muted-foreground",
        },
      }}
      {...props}
    />
  )
}

export { Toaster }
